import {
	Button,
	ButtonGroup,
	Checkbox,
	CheckboxGroup,
	Dialog,
	IconButton,
	LuIcon,
	Separator,
	stopPropagation,
	Text,
} from "@overmap-ai/blocks"
import { EMPTY_OBJECT, type AssetType } from "@overmap-ai/common"
import { selectAssetTypeIdentifiersOfAssetType } from "@overmap-ai/core"
import * as RadixCollapsible from "@radix-ui/react-collapsible"
import { memo, useCallback, useReducer } from "react"

import { ASSET_PDF_EXPORT_OPTIONS } from "../../constants"
import { modelIconMapping } from "../../mappings/icons"
import { assetPdfExportOptionsData } from "../../mappings/reports"
import { useShallowEqaulSelector } from "../../store"
import type { AssetPdfExportOptions } from "../../typings/reports"
import type { ExtendableDialogProps } from "./typings"

function reducer(
	state: AssetPdfExportOptions,
	action:
		| {
				[Option in keyof AssetPdfExportOptions]-?: {
					type: Option
					payload: NonNullable<AssetPdfExportOptions[Option]>
				}
		  }[keyof AssetPdfExportOptions]
		| { type: "reset" },
) {
	switch (action.type) {
		case "reset":
			return {}
		default:
			return { ...state, [action.type]: action.payload }
	}
}

interface AssetPdfExportOptionsDialogProps extends ExtendableDialogProps {
	assetTypeId: AssetType["uuid"]
	onSubmitOptions: (options: AssetPdfExportOptions) => void
}

export const AssetPdfExportOptionsDialog = memo<AssetPdfExportOptionsDialogProps>((props) => {
	const {
		children,
		open,
		onOpenChange, // oxlint-disable-line unbound-method
		modal,
		defaultOpen,
		size,
		onSubmitOptions,
		assetTypeId,
		...rest
	} = props

	const identifiers = useShallowEqaulSelector(selectAssetTypeIdentifiersOfAssetType(assetTypeId))

	const [state, dispatch] = useReducer(reducer, EMPTY_OBJECT)

	const handleReset = useCallback(() => {
		dispatch({ type: "reset" })
	}, [])

	const handleSubmitOptions = useCallback(() => {
		onSubmitOptions(state)
	}, [state, onSubmitOptions])

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange} modal={modal} defaultOpen={defaultOpen}>
			{!!children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}
			<Dialog.Overlay onClick={stopPropagation} />
			<Dialog.Content
				className="flex max-h-[85dvh] flex-col gap-4 overflow-hidden"
				size={size}
				onClick={stopPropagation}
				{...rest}
			>
				<div className="flex shrink-0 flex-col gap-1">
					<Dialog.Title>
						<Text size="lg">Export assets to PDF</Text>
					</Dialog.Title>
					<Dialog.Description asChild>
						<Text accentColor="base" size="sm">
							Choose which asset details to include in the PDF report.
						</Text>
					</Dialog.Description>
				</div>
				<Separator className="shrink-0" size="full" />
				<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
					{ASSET_PDF_EXPORT_OPTIONS.map((option) => {
						const { label, description } = assetPdfExportOptionsData[option]
						switch (option) {
							case "asset_type_identifiers": {
								const allValues = identifiers.map((item) => item.uuid)

								return (
									<RadixCollapsible.Root defaultOpen={false} key={option} asChild>
										<CheckboxGroup.Root
											className="flex flex-col gap-2"
											aria-label={label}
											allValues={allValues}
											values={state[option] ?? allValues}
											onValuesChange={(payload) => dispatch({ type: option, payload })}
											size="sm"
										>
											<div className="flex items-start gap-2">
												<label className="flex flex-1 items-start gap-2">
													<CheckboxGroup.SelectAllItem
														aria-label={label}
														disabled={identifiers.length === 0}
														className="group/select-all"
													>
														<CheckboxGroup.ItemIndicator>
															<LuIcon
																icon={modelIconMapping.check}
																className="group-data-[state=indeterminate]/select-all:hidden"
															/>
															<LuIcon
																icon={modelIconMapping.minus}
																className="hidden group-data-[state=indeterminate]/select-all:block"
															/>
														</CheckboxGroup.ItemIndicator>
													</CheckboxGroup.SelectAllItem>
													<span className="flex flex-col">
														<Text>{label}</Text>
														<Text accentColor="base" size="sm">
															{description}
														</Text>
													</span>
												</label>
												<RadixCollapsible.Trigger asChild>
													<IconButton
														type="button"
														aria-label={`Choose ${label.toLowerCase()}`}
														className="group shrink-0"
														variant="ghost"
														accentColor="base"
														size="sm"
													>
														<LuIcon
															icon={modelIconMapping.chevronRight}
															className="group-data-[state=open]:rotate-90"
														/>
													</IconButton>
												</RadixCollapsible.Trigger>
											</div>
											<RadixCollapsible.Content className="ml-6 flex flex-col gap-2">
												{identifiers.length === 0 ? (
													<Text accentColor="base" size="sm">
														No identifiers are configured for this asset type.
													</Text>
												) : (
													identifiers.map((item) => (
														<label className="flex items-start gap-2" key={item.uuid}>
															<CheckboxGroup.Item value={item.uuid}>
																<CheckboxGroup.ItemIndicator>
																	<LuIcon icon={modelIconMapping.check} />
																</CheckboxGroup.ItemIndicator>
															</CheckboxGroup.Item>
															<Text size="sm">{item.name}</Text>
														</label>
													))
												)}
											</RadixCollapsible.Content>
										</CheckboxGroup.Root>
									</RadixCollapsible.Root>
								)
							}
							default:
								return (
									<label className="flex items-start gap-2" key={option}>
										<Checkbox.Root
											aria-label={label}
											checked={state[option] ?? true}
											onCheckedChange={(value) =>
												dispatch({ type: option, payload: value === true })
											}
											size="sm"
										>
											<Checkbox.Indicator>
												<LuIcon icon={modelIconMapping.check} />
											</Checkbox.Indicator>
										</Checkbox.Root>
										<span className="flex flex-col">
											<Text>{label}</Text>
											<Text accentColor="base" size="sm">
												{description}
											</Text>
										</span>
									</label>
								)
						}
					})}
				</div>
				<Separator className="shrink-0" size="full" />
				<ButtonGroup className="flex shrink-0 justify-between gap-2" size="sm">
					<Button
						onClick={handleReset}
						accentColor="base"
						variant="ghost"
						disabled={Object.keys(state).length === 0}
					>
						<LuIcon icon={modelIconMapping.refresh} /> Reset
					</Button>
					<Dialog.Close asChild>
						<Button onClick={handleSubmitOptions} accentColor="primary" variant="surface">
							<LuIcon icon={modelIconMapping.check} /> Submit
						</Button>
					</Dialog.Close>
				</ButtonGroup>
			</Dialog.Content>
		</Dialog.Root>
	)
})

AssetPdfExportOptionsDialog.displayName = "AssetPdfExportOptionsDialog"
